import React, { useEffect, useRef } from 'react';
import { Box, Flex } from '@chakra-ui/react';

import { useMessages } from '../../../redux/hooks/answer/conversation-hooks';
import { useAppSelector } from '../../../redux/hooks/app-hooks';
import { MessageLoading } from '../Message/Loading/Loading';

import styles from '../../../styles/chakra/theme.module.scss';

export type MessagesInfiniteScrollLoaderProps = {
    children?: React.ReactNode;
    className?: string;
};

const MessagesInfiniteScrollLoaderComponent: React.FC<MessagesInfiniteScrollLoaderProps> = ({
    children,
    className,
}) => {
    const activeConversationId = useAppSelector((state) => state.chatTab.activeConversationId) ?? '';
    const { hasNextPage, isFetchingNextPage, fetchNextPage } = useMessages(activeConversationId);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const loaderRef = useRef<HTMLDivElement>(null);
    const ioRef = useRef<IntersectionObserver | null>(null);
    const prevScrollPosition = useRef<number>();

    useEffect(() => {
        if (!hasNextPage) {
            return;
        }

        const root = wrapperRef.current;
        const loader = loaderRef.current;

        if (!(root && loader && !ioRef.current)) {
            return;
        }

        const io = new IntersectionObserver(
            async (entries, observer) => {
                const entry = entries[0];

                const wrapper = wrapperRef.current;
                if (entry?.isIntersecting && wrapper) {
                    observer.unobserve(entry.target);

                    await fetchNextPage();

                    queueMicrotask(() => {
                        observer.observe(entry.target);
                    });
                }
            },
            { root, rootMargin: `${loader.clientHeight}px 0px 0px 0px` }
        );
        ioRef.current = io;
        io.observe(loader);

        return () => {
            io.disconnect();
            ioRef.current = null;
        };
    }, [hasNextPage, fetchNextPage]);

    useEffect(() => {
        const io = ioRef.current;
        const loader = loaderRef.current;
        const wrapper = wrapperRef.current;

        if (io && wrapper && loader) {
            if (isFetchingNextPage) {
                prevScrollPosition.current = wrapper.scrollHeight - wrapper.scrollTop;
            } else if (prevScrollPosition.current) {
                wrapper.scrollTop = wrapper.scrollHeight - prevScrollPosition.current;
            }
        }
    }, [isFetchingNextPage]);

    return (
        <Box ref={wrapperRef} className={className}>
            {hasNextPage && (
                <Flex justifyContent="center" ref={loaderRef}>
                    <MessageLoading dotsCSSColor={styles.textMidGray} />
                </Flex>
            )}
            {children}
        </Box>
    );
};

export const MessagesInfiniteScrollLoader = React.memo(MessagesInfiniteScrollLoaderComponent);
